"use client";

import { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import type { Dog } from "@/lib/types";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useAnalytics } from '@/hooks/use-analytics';

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, CheckCircle2 } from "lucide-react";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters."),
  email: z.string().email("Please enter a valid email."),
  phoneNumber: z.string().min(10, "Please enter a valid phone number."),
  message: z.string().min(10, "Message must be at least 10 characters.").max(500, "Message must be less than 500 characters."),
  subscribeToNewsletter: z.boolean().default(true),
  website: z.string().optional(),
});

interface InquiryFormProps {
  dog?: Dog;
  onFormSubmit: () => void;
  onCancel?: () => void;
  isEmbedded?: boolean;
  hideHeader?: boolean;
}

export function InquiryForm({ dog, onFormSubmit, onCancel, isEmbedded = false, hideHeader = false }: InquiryFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { logEvent } = useAnalytics();
  const createInquiry = useMutation((api as any).inquiries?.create || (() => Promise.resolve()));
  const subscribeToMailingList = useMutation(api.mailingList.subscribe);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      message: dog ? `I'm interested in ${dog.name}!` : "I'd like to join your waiting list for upcoming litters!",
      subscribeToNewsletter: true,
      website: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (values.website) {
      console.log("Honeypot field filled. Likely spam.");
      onFormSubmit();
      return;
    }

    setIsSubmitting(true);

    try {
      // Parallel execution for speed
      const promises = [
        createInquiry({
          name: values.name,
          email: values.email,
          phoneNumber: values.phoneNumber,
          message: values.message,
          dogId: dog?.id || 'general',
          dogName: dog?.name || 'General Waiting List',
          status: 'new',
        })
      ];

      // If subscribed, add to mailing list
      if (values.subscribeToNewsletter) {
        promises.push(
          subscribeToMailingList({
            email: values.email,
            name: values.name,
            source: dog ? `inquiry_${dog.name}` : 'inquiry_general'
          }).catch(e => console.error("Mailing list subscription failed silently", e))
        );
      }

      await Promise.all(promises);

      // Track inquiry
      logEvent(dog?.id, 'inquiry_submit');

      toast({
        title: "Inquiry Sent!",
        description: dog
          ? "We've received your message and will get back to you shortly."
          : "We've received your inquiry. We'll be in touch!",
      });

      onFormSubmit();
    } catch (error) {
      console.error("Inquiry submission error:", error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "There was a problem sending your inquiry. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const titleText = dog ? `Inquire about ${dog.name}` : 'General Inquiry';
  const descriptionText = dog
    ? "Fill out the form below and we'll get in touch with you shortly."
    : "Tell us what you are looking for and we'll get back to you.";

  return (
    <div className="flex flex-col h-full">
      {!hideHeader && (
        <DialogHeader className={isEmbedded ? "mb-6" : ""}>
          {isEmbedded ? (
            <>
              <h2 className="text-xl font-bold leading-none tracking-tight text-foreground">{titleText}</h2>
              <p className="text-sm text-muted-foreground mt-2">{descriptionText}</p>
            </>
          ) : (
            <>
              <DialogTitle>{titleText}</DialogTitle>
              <DialogDescription>{descriptionText}</DialogDescription>
            </>
          )}
        </DialogHeader>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 flex-grow overflow-y-auto px-1">
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem className="absolute left-[-9999px]">
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input {...field} tabIndex={-1} autoComplete="off" />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold text-foreground">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} className="h-12 rounded-2xl bg-background/50 border-input focus:ring-primary/20" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold text-foreground">Email Address</FormLabel>
                  <FormControl>
                    <Input placeholder="you@example.com" {...field} className="h-12 rounded-2xl bg-background/50 border-input focus:ring-primary/20" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-semibold text-foreground">Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="(123) 456-7890" {...field} className="h-12 rounded-2xl bg-background/50 border-input focus:ring-primary/20" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-semibold text-foreground">Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us a bit about yourself and what you're looking for..." {...field} className="min-h-[120px] rounded-2xl bg-background/50 border-input focus:ring-primary/20 resize-none p-4" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subscribeToNewsletter"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="w-5 h-5 border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white rounded-md"
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-semibold text-foreground cursor-pointer">
                    Stay updated on new litters
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />

          <DialogFooter className="pt-2 mt-auto gap-3 sm:gap-0">
            {onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting} className="rounded-full h-12">Back</Button>
            )}
            <Button type="submit" disabled={isSubmitting} className="rounded-full h-12 px-8 font-bold text-md shadow-luxury hover:shadow-xl hover:scale-[1.02] transition-all w-full sm:w-auto">
              {isSubmitting ? (
                <span className="flex items-center gap-2">Processing...</span>
              ) : (
                <span className="flex items-center gap-2">Send Inquiry <Send className="w-4 h-4" /></span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
